import { useCallback, useEffect, useMemo, useState } from 'react';
import { CommonBackContent } from '../Layouts/CommonBackContent';
import { useConfigProvider } from '@/context/ConfigProvider';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import Image from 'next/image';
import styled from 'styled-components';
import { cn } from '@/lib/utils';
import { HelpCenterCategoryItem } from '@/types/help';
import { getHelpCenterCategoryList } from '@/lib/service/help';
import { Button } from 'antd';
import { ArrowRightOutlined, RightOutlined } from '@ant-design/icons';
import Link from 'next/link';
import { AppSettings } from '@/shared/app-common';
import HelpCenterContactFooter from './HelpCenterContactFooter';

interface FaqItem {
  title: string;
  title_id?: string;
  desc: string;
  image?: string;
  path?: string;
  contents?: FaqItem[];
}

interface LabelItem {
  label: string;
  faqs: FaqItem[];
}

const RichTextContent = styled.div`
  a {
    color: #0066cc;
    text-decoration: underline;
  }
  a:hover {
    color: var(--app-primary-color);
  }
`;

const featureToggle = {
  headerSearch: false,
  headerTabs: true,
};

const HelpCenter = ({ data }: { data?: any }) => {
  const { t } = useTranslation('common');
  const { isBlogWeb, isSupportWeb, isBlogOrSupportWeb } = useConfigProvider();
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const [contentData] = useState<LabelItem[]>(data?.contentData?.contentInfo?.hero?.content);
  // 使用传入的数据或默认空数组
  const labels = contentData || [];
  const [categorys, setCategorys] = useState<LabelItem[]>(data.formatedCategorys);

  const tabs = useMemo(() => {
    return [
      {
        image: '/images/help/support-center.webp',
        label: t('Support Center'),
        subLabel: t('Plans, Features & Account Help'),
        desc: t(
          'Find answers to common questions, learn how to use key features, and manage your account with ease. Start here to get the most out of Dolphin Radar.'
        ),
        value: 'faq',
        href: isBlogOrSupportWeb ? `${AppSettings.webHost}/instagram-tracking-faq` : `/instagram-tracking-faq`,
        isLink: isBlogOrSupportWeb,
        ready: true,
      },
      {
        image: '/images/help/instagram-resources.webp',
        label: t('Instagram Resources'),
        subLabel: t('Instagram Tips & Strategy Guides'),
        desc: t(
          "Practical guides to help you master Instagram — from planning and posting to growth and engagement. Start learning, whether you're new or growing a brand."
        ),
        value: 'howTo',
        href: isSupportWeb ? `/` : `${AppSettings.supportHost}/`,
        isLink: !isSupportWeb,
        ready: categorys?.length >= 0,
      },
    ];
  }, [categorys?.length, isBlogOrSupportWeb, isSupportWeb]);

  // 从路由参数中获取初始活动选项卡和标签值
  const [activeTabKey, setActiveTabKey] = useState(
    (router.query.tab as string) || data.initialActiveTab || tabs[0].value
  );
  const [activeLabel, setActiveLabel] = useState<string | null>((router.query.label as string) || labels?.[0]?.label);

  const activeTab = useMemo(() => {
    return tabs.find((tab) => tab.value === activeTabKey);
  }, [activeTabKey, tabs]);

  // 更新URL查询参数而不刷新页面
  const updateQueryParam = useCallback(
    (key: string, value: string | null, shallow: boolean = true) => {
      // 获取当前查询参数
      const newQuery = { ...router.query };

      if (value === null) {
        // 如果值为null，则删除该参数
        delete newQuery[key];
      } else {
        // 否则更新该参数
        newQuery[key] = value;
      }

      // 使用 push 更新 URL，且不刷新页面
      router.push(
        {
          pathname: router.pathname, // 保持当前路径
          query: newQuery, // 更新查询参数
        },
        undefined,
        { shallow: shallow }
      ); // shallow: true 确保不触发页面刷新
    },
    [router]
  );

  // 处理标签切换
  const handleLabelChange = useCallback(
    (value: string) => {
      setActiveLabel(value);
      updateQueryParam('label', value);
    },
    [updateQueryParam]
  );

  // 同步URL参数到状态
  useEffect(() => {
    if (router.isReady) {
      // 同步tab参数
      if (router.query.tab && router.query.tab !== activeTabKey) {
        setActiveTabKey(router.query.tab as string);
      }

      // 同步label参数
      if (router.query.label && router.query.label !== activeLabel) {
        setActiveLabel(router.query.label as string);
      }
    }
  }, [router.isReady, router.query]);

  useEffect(() => {
    if (isBlogWeb) {
      router.push('/instagram-tracking-blog');
    }
  }, [isBlogWeb, router]);

  // 根据搜索查询过滤FAQ内容
  const filteredFAQLabels = useMemo(() => {
    if (!searchQuery.trim()) {
      return labels;
    }

    return labels
      .map((label) => ({
        ...label,
        faqs: label.faqs.filter(
          (faq) =>
            faq.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.desc.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      }))
      .filter((label) => label.faqs.length > 0);
  }, [labels, searchQuery, activeTabKey]);
  const filteredHowToLabels = useMemo(() => {
    if (!searchQuery.trim()) {
      return categorys || [];
    }

    return categorys?.filter((label) =>
      label.faqs.some(
        (faq) =>
          faq.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.desc.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.contents?.some(
            (content) =>
              content.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
              content.desc.toLowerCase().includes(searchQuery.toLowerCase())
          )
      )
    );
  }, [categorys, searchQuery]);
  const filteredLabels = useMemo(() => {
    if (activeTabKey === 'howTo') {
      return filteredHowToLabels;
    }
    return filteredFAQLabels;
  }, [filteredFAQLabels, filteredHowToLabels, activeTabKey]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // 获取帮助中心分类列表
  const fetchCategorys = useCallback(async () => {
    const res = await getHelpCenterCategoryList();
    const formatedCategorys = res.data.map((item) => ({
      // 一级分类标题
      label: item.name,
      faqs: item.children?.map((child) => ({
        // 二级分类标题
        title: child.name,
        desc: child.description,
        image: child.image,
        path: `${router.pathname}/${child.name_id}`,
        contents: child.contents?.map((content) => ({
          // 文章标题
          title: content.title,
          desc: content.tags?.join(','),
          path: `${router.pathname}/${child.name_id}/${content.title_id}`,
        })),
      })),
    }));
    setCategorys(formatedCategorys);
  }, []);

  const renderSearchInput = (className?: string) => {
    return (
      <div className={`relative ${className}`}>
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 -mt-0.5 text-black-500">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearch}
          placeholder={t('Ask a question')}
          className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[var(--app-primary-color)] bg-white"
        />
      </div>
    );
  };

  const renderHeaderTabs = useCallback(() => {
    const renderTabContent = (tab: any) => {
      return (
        <>
          <Image
            src={tab.image}
            alt={tab.label}
            width={64}
            height={64}
            className="flex-shrink-0 w-[38px] h-[38px] md:w-[64px] md:h-[64px] object-contain"
          />
          <div className="space-y-0.5">
            <h2
              className={cn('text-[18px] leading-[24px] text-white transition-colors max-md:text-base', {
                'text-[var(--app-primary-color)]': activeTabKey === tab.value,
              })}
            >
              {tab.label}
            </h2>
            <p
              className={cn('text-[14px] leading-[24px] text-white opacity-60 transition-colors max-md:text-xs', {
                'text-[var(--app-primary-color)]': activeTabKey === tab.value,
              })}
            >
              {tab.subLabel}
            </p>
          </div>
        </>
      );
    };

    return (
      <div className="flex flex-col items-center justify-center gap-10 max-w-[880px] mt-14 text-center max-md:max-w-[calc(100vw-40px)] max-md:mt-6 max-md:gap-5">
        <div className="flex items-center gap-5 max-md:gap-2">
          {tabs.map((tab) =>
            tab.isLink ? (
              <Link
                key={tab.value}
                href={tab.href}
                target="_blank"
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5 md:w-[280px] h-[144px] rounded-xl bg-[#827EE9] cursor-pointer',
                  'max-md:flex-row max-md:h-full max-md:min-h-max max-md:py-3 max-md:text-left px-2.5'
                )}
              >
                {renderTabContent(tab)}
              </Link>
            ) : (
              <div
                key={tab.value}
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5 md:w-[280px] h-[144px] rounded-xl bg-white cursor-pointer',
                  'max-md:flex-row max-md:h-full max-md:min-h-max max-md:py-3 max-md:text-left px-2.5'
                )}
              >
                {renderTabContent(tab)}
              </div>
            )
          )}
        </div>
        {/* 当前描述 */}
        <p className="text-[16px] leading-[24px] text-white max-md:text-[13px]">{activeTab?.desc}</p>
      </div>
    );
  }, [activeTab?.desc, activeTabKey, tabs, updateQueryParam, activeLabel, fetchCategorys]);

  const getContent = useMemo(() => {
    const renderHowToGroup = (label: LabelItem) => {
      return (
        <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1 py-4">
          {label.faqs.map((item) => (
            <div key={'group:' + item.title} className="flex flex-col justify-between">
              <div className="flex flex-col">
                {/* 三级分类图片 */}
                <Link href={item.path} className="h-[216px] border border-gray-200 rounded-lg overflow-hidden">
                  {item.image ? (
                    <Image
                      src={item.image}
                      alt={item.title}
                      width={396}
                      height={216}
                      className="max-h-[216px] object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-[216px] bg-gray-200"></div>
                  )}
                </Link>
                {/* 三级分类标题 */}
                <Link href={item.path} className="mt-6 text-[20px] leading-[24px] font-bold">
                  {item.title}
                </Link>
                {/* 文章列表 */}
                {item.contents && (
                  <div className="mt-4 text-[16px] leading-[30px]">
                    {item.contents?.slice(0, 3).map((child) => (
                      <Link
                        key={'child:' + child.title}
                        href={child.path}
                        className="block text-[16px] leading-[30px] hover:underline"
                      >
                        {child.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              {/* 按钮 */}
              {item.contents.length >= 3 && (
                <div className="mt-4">
                  <Link href={item.path}>
                    <Button className="w-[138px] h-[42px]">
                      {`${item.contents?.length || ''} ${t('more')}`}
                      <ArrowRightOutlined className="text-[var(--app-primary-color)]" />
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    };

    return (
      <>
        {/* 头部banner */}
        <div className="relative">
          <div className="hidden md:block">
            <Image
              src="/images/help/helpcenter-bg.webp"
              alt="Help Center Background"
              width={1920}
              height={500}
              className="w-full h-[500px] object-cover"
            />
          </div>
          <div className="block md:hidden">
            <Image
              src="/images/help/helpcenter-bg-mobile.webp"
              alt="Help Center Background Mobile"
              width={768}
              height={324}
              className="w-full h-[324px] object-cover"
            />
          </div>

          <div className="absolute inset-0 flex flex-col items-center justify-center max-md:px-5">
            {/* 移动端字体大小改为42 */}
            <h1 className="text-4xl md:text-5xl text-white">
              {t('How Can We')} <span className="font-bold">{t('Help?')}</span>
            </h1>
            {featureToggle.headerSearch && (
              <div className="w-full px-4 flex justify-center mt-[30px] md:mt-[70px]">
                <div className="w-[90%] md:w-1/2 relative">{renderSearchInput()}</div>
              </div>
            )}
            {/* 选项卡 */}
            {featureToggle.headerTabs && renderHeaderTabs()}
          </div>
        </div>

        {/* 主体内容 */}
        <CommonBackContent style={{ minHeight: '100vh' }}>
          <div className="md:px-6 lg:px-8">
            {/* 添加隐藏的语义化结构 */}
            <div className="sr-only" aria-hidden="true">
              {labels.map((label) => (
                <div key={`semantic-${label.label}`}>
                  <h3>{label.label}</h3>
                  {label.faqs.map((item) => (
                    <div key={`semantic-${item.title}`}>
                      <h4>{item.title}</h4>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* 搜索栏 */}
            <div className="mt-8 mb-9 flex justify-between items-center max-md:flex-col max-md:items-start max-md:gap-5 max-md:mt-2 max-md:mb-5">
              {/* 标题和描述 */}
              <div className="space-y-0.5">
                <h2 className="text-xl font-semibold max-md:text-[18px] max-md:leading-6 max-md:font-bold">
                  {activeTab.label}
                </h2>
                <p className="text-gray-500 max-md:text-[14px] leading-6">{activeTab.subLabel}</p>
              </div>
              {/* 搜索框 */}
              {renderSearchInput('w-[371px] h-[54px] max-md:w-full')}
            </div>

            {searchQuery.trim() && (
              <div className="mt-8 mb-4">
                <h2 className="text-xl font-semibold">
                  {t('Search results for')} "{searchQuery}"
                </h2>

                {filteredLabels.length === 0 && (
                  <div className="flex items-center mt-4 mb-4 rounded-lg">
                    <div className="mr-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="#FF4D4F"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </div>
                    <p>{t("We couldn't find any results for your search")}</p>
                  </div>
                )}
              </div>
            )}

            {/* 中部内容 */}
            <div className="flex flex-col md:flex-row md:mt-5">
              {/* 桌面端显示 Tabs */}
              <div className="hidden md:block w-full">
                {filteredLabels.length > 0 ? (
                  <Tabs
                    defaultValue={activeLabel || filteredLabels[0]?.label}
                    value={activeLabel || filteredLabels[0]?.label}
                    orientation="vertical"
                    className="w-full flex flex-col md:flex-row"
                    onValueChange={handleLabelChange}
                  >
                    <div className="w-full md:w-[30%] border-r border-transparent">
                      <TabsList className="flex md:flex-col items-start h-100 p-0 w-full overflow-x-auto md:overflow-visible bg-transparent">
                        {filteredLabels.map((label) => (
                          <TabsTrigger
                            key={'tab:' + label.label}
                            value={label.label}
                            className="w-[316px] h-[68px] text-left justify-start font-bold text-[16px] px-4 py-2 mb-2 bg-[#F8F8F8] data-[state=active]:bg-[#F8F8F8] data-[state=active]:border-l-4 data-[state=active]:border-[#725CFF] rounded-none"
                          >
                            {label.label}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                    </div>

                    {activeTabKey === 'faq' && (
                      <div className="w-full md:w-[70%] pl-0 md:pl-2 mt-4 md:mt-0">
                        {filteredLabels.map((label) => (
                          <TabsContent key={label.label} value={label.label} className={'mt-0'}>
                            <Accordion type="single" collapsible className="border-none shadow-none">
                              {label.faqs.map((item, itemIndex) => (
                                <AccordionItem
                                  key={'accordion:' + item.title}
                                  value={itemIndex.toString()}
                                  className="mb-2 bg-white rounded-lg shadow-[0_0_10px_#E3E6F3] w-full"
                                >
                                  <AccordionTrigger className="px-4 py-3 text-[18px] font-normal h-[68px]">
                                    {item.title}
                                  </AccordionTrigger>
                                  <AccordionContent className="px-4 pb-4 text-[16px] font-normal leading-[1.5]">
                                    <RichTextContent dangerouslySetInnerHTML={{ __html: item.desc }} />
                                  </AccordionContent>
                                </AccordionItem>
                              ))}
                            </Accordion>
                          </TabsContent>
                        ))}
                      </div>
                    )}
                    {activeTabKey === 'howTo' && (
                      <div className="w-full md:w-[70%] pl-0 md:pl-2 mt-4 md:mt-0">
                        {filteredLabels.map((label) => (
                          <TabsContent key={label.label} value={label.label} className={'mt-0'}>
                            {renderHowToGroup(label)}
                          </TabsContent>
                        ))}
                      </div>
                    )}
                  </Tabs>
                ) : (
                  <div className="w-full text-center py-10">
                    {!searchQuery.trim() && <p className="text-lg text-gray-500">{t('No results found')}</p>}
                  </div>
                )}
              </div>

              {/* 移动端显示二级 Accordion */}
              <div className="block md:hidden w-full">
                {filteredLabels.length > 0 ? (
                  <>
                    {activeTabKey === 'faq' && (
                      <Accordion
                        type="single"
                        collapsible
                        className="border-none shadow-none"
                        defaultValue={activeLabel || undefined}
                        value={activeLabel || undefined}
                        onValueChange={handleLabelChange}
                      >
                        {filteredLabels.map((label) => (
                          <AccordionItem key={label.label} value={label.label} className="mt-2 border-none">
                            <AccordionTrigger className="px-4 py-3 text-[16px] font-bold h-[68px] bg-[#F8F8F8] accordion-trigger [&>svg]:hidden data-[state=open]:border-l-4 data-[state=open]:border-[#725CFF]">
                              <span>{label.label}</span>
                            </AccordionTrigger>
                            <AccordionContent className="px-0 pb-0">
                              <Accordion type="single" collapsible className="border-none shadow-none">
                                {label.faqs.map((item, itemIndex) => (
                                  <AccordionItem
                                    key={'accordion:' + item.title}
                                    value={itemIndex.toString()}
                                    className="mx-2 bg-white"
                                  >
                                    <AccordionTrigger className="px-4 py-4 text-[16px] leading-[1.2] font-normal">
                                      {item.title}
                                    </AccordionTrigger>
                                    <AccordionContent className="px-4 pb-3 text-[14px] font-normal">
                                      <RichTextContent dangerouslySetInnerHTML={{ __html: item.desc }} />
                                    </AccordionContent>
                                  </AccordionItem>
                                ))}
                              </Accordion>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    )}
                    {activeTabKey === 'howTo' && (
                      <Accordion
                        type="single"
                        collapsible
                        className="border-none shadow-none"
                        defaultValue={activeLabel || undefined}
                        value={activeLabel || undefined}
                        onValueChange={handleLabelChange}
                      >
                        {filteredLabels.map((label) => (
                          <AccordionItem key={label.label} value={label.label} className="mt-2 border-none">
                            <AccordionTrigger className="px-4 py-3 text-[16px] font-bold h-[68px] bg-[#F8F8F8] accordion-trigger [&>svg]:hidden data-[state=open]:border-l-4 data-[state=open]:border-[#725CFF]">
                              <span>{label.label}</span>
                            </AccordionTrigger>
                            <AccordionContent className="px-0 pb-0">{renderHowToGroup(label)}</AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    )}
                  </>
                ) : (
                  <div className="w-full text-center py-10">
                    {!searchQuery.trim() && <p className="text-lg text-gray-500">{t('No results found')}</p>}
                  </div>
                )}
              </div>
            </div>

            {/* 添加底部联系区域 */}
            <HelpCenterContactFooter className="mt-12 mb-8" />
          </div>
        </CommonBackContent>
      </>
    );
  }, [filteredLabels, searchQuery, renderHeaderTabs, activeTabKey, activeLabel, handleLabelChange]);

  if (isBlogWeb) {
    return <></>;
  }
  return <>{getContent}</>;
};

export default HelpCenter;
